import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FilterService } from '../../../../../_shared/services';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { ExecutiveStatsSummaryRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { HorizontalGroupedBarChartComponent, HorizontalGroupedBarChartData } from '../../../../../_shared/components/graphs/horizontal-grouped-bar-chart/horizontal-grouped-bar-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-violations-widget',
  standalone: true,
  imports: [CommonModule, HorizontalGroupedBarChartComponent, GraphTrendIconComponent, EmptyStateComponent, GraphErrorStateComponent, TranslocoDirective],
  templateUrl: './violations-widget.component.html',
  styleUrl: './violations-widget.component.scss'
})
export class ViolationsWidgetComponent extends BaseWidgetComponent {
  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  public hasError: boolean = false;
  public errorMessage: string = '';
  public hasResult: boolean = false;
  public chartData: HorizontalGroupedBarChartData[] = [];
  readonly chartHeight = 175;

  readonly subgroups = [
    { name: 'You', legend: this.transloco.translate('driverOverview.chart.you'), color: '#FF7F00' },
    { name: 'Globaltrends', legend: this.transloco.translate('executiveStats.chart.globalTrends'), color: '#3873C9' }
  ];

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.isLoading = true;
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;
    try {
      const request: ExecutiveStatsSummaryRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
      };

      this.cancelSubject.next();
      const [violations, violationsInsights, globalViolations] = await Promise.all([
        this.executiveStatsService.getViolations(request, this.cancelSubject),
        this.executiveStatsService.getViolationsInsightsSummary(request, this.cancelSubject),
        this.executiveStatsService.getGlobalViolations(request, this.cancelSubject)
      ]);

      const labels = violations.data?.map(x => x.violationName) || [];
      const youData = violations.data?.map(x => x.totalViolations) || [];
      const globalData = globalViolations.data?.map(x => x.totalViolations) || [];

      this.chartData = labels.map((label, i) => ({
        label,
        You: youData[i] || 0,
        Globaltrends: globalData[i] || 0
      }));

      this.value = violationsInsights.data?.totalValue ?? 0;
      this.percentageDifference = violationsInsights.data?.percentageDifference ?? 0;
      this.hasResult = youData.some(x => x > 0) || globalData.some(x => x > 0);

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.hasError = true;
          this.errorMessage = message;
          this.isLoading = false;
        }
      });
    }
  }

  public override formatValue(value: number): string {
    return `${value.toLocaleString()}`;
  }

  public formatDifference(value: number): string {
    return `${value.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  }

  formatPercentChange(): string {
    const sign = this.percentageDifference > 0 ? '+' : '';
    return `${sign}${this.percentageDifference.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatTotalValue(): string {
    return `${this.value.toLocaleString()}`;
  }

  isUptrendPositive(): boolean {
    return false;
  }

  hasData(): boolean {
    return this.hasResult && this.chartData.length > 0;
  }
}